import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { checkSession, logout as apiLogout } from '../services/api';
import { UserSession } from '../types';

interface AuthContextType {
  session: UserSession | null;
  loading: boolean;
  error: string | null;
  login: () => void;
  demoLogin: () => void;
  logout: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<UserSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const login = useCallback(() => {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.replace(`/auth/login?return_to=${returnTo}`);
  }, []);

  const demoLogin = useCallback(() => {
    setSession({
      authenticated: true,
      userName: 'Solar Engineer (Demo)',
      userEmail: 'engineer@bigsunsolar.com',
      userId: 'usr-demo-01',
    });
  }, []);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const res = await checkSession();
      if (res.authenticated) {
        setSession({
          authenticated: true,
          userEmail: res.data?.email || res.data?.userEmail,
          userName: res.data?.name || res.data?.userName,
          userId: res.data?.id || res.data?.userId,
        });
        setError(null);
        return true;
      } else {
        setSession(null);
        return false;
      }
    } catch (err: any) {
      setError(err?.message || 'Authentication check failed.');
      setSession(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await apiLogout();
    setSession(null);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // 1. SSO token redirect
    const bscToken = urlParams.get('bsc_token');
    if (bscToken) {
      window.location.replace(`/auth/callback?${urlParams.toString()}`);
      return;
    }

    // 2. Auth error
    if (urlParams.has('auth_error')) {
      setError('Authentication failed. Please reload the page to try again.');
      setLoading(false);
      return;
    }

    // 3. Session error
    if (urlParams.has('session_error')) {
      window.history.replaceState({}, document.title, window.location.pathname);
      setError('Helios could not keep your secure session. Please sign in again.');
      setLoading(false);
      return;
    }

    // 4. Check active session
    refreshSession();
  }, [refreshSession]);

  return (
    <AuthContext.Provider
      value={{
        session,
        loading,
        error,
        login,
        demoLogin,
        logout,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
